import { Component, OnInit, Inject, HostListener, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { DOCUMENT } from '@angular/common';
import { Observable } from  "rxjs";
import { debounceTime } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ITrialService } from '../../services/Trial/trial.service.interface';
import { Router } from '@angular/router';
import { TrialRegistration } from '../../models/trialRegistration';
import { AuthenticationService } from '../../services/auth.service';
import { PaymentService } from '../../services/payment/payment.service';

@Component({
  selector: 'app-trial-register-account',
  templateUrl: './trial-register-account.component.html',
  styleUrls: ['./trial-register-account.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [PaymentService]
})
export class TrialRegisterAccountComponent implements OnInit {

  document: Document;
  scrollPosition: number;
  fixHeaderAt: number;
  trialService: ITrialService;
  loginPrefix: string;
  isNewCustomer: boolean = true;
  isStripeCardValid: boolean = false;
  showErrorMessage: boolean = false;
  planSelected: string = 'catalyst';
  usernamePrefix: string = '';
  organizationData: any;
  loginData: any;
  
  activeStep: number;
  prevStep: number;
  trialData: TrialRegistration;
  organizationForm: FormGroup;
  logInForm: FormGroup;
  paymentForm: FormGroup;
 
  shouldShowModal : boolean = false;
  shouldShowSpinner : boolean = false;

  constructor(private paymentService: PaymentService, private authService: AuthenticationService, private http: HttpClient, trialService: ITrialService, formBuilder: FormBuilder, @Inject(DOCUMENT) document: Document, private router: Router) { 
    this.trialService = trialService;
    this.scrollPosition = 0;
    this.document = document;
    this.fixHeaderAt = 240;
    this.activeStep = 1;
    this.prevStep = 1;
    this.isStripeCardValid = false;
    this.showErrorMessage = false;
    this.planSelected = 'catalyst';
    this.usernamePrefix = '';
    this.shouldShowModal = false;
    this.shouldShowSpinner = false;

    this.organizationForm = formBuilder.group({
      terms: new FormControl('', Validators.requiredTrue)
    });

    this.logInForm = formBuilder.group({});
    this.paymentForm = formBuilder.group({});

    paymentService.getPaymentMethodSuccess$.subscribe(paymentMethodId => this.startTrialWithPaymentMethod(paymentMethodId));
  }

  onSubformReady(name: string, form: FormGroup) {

    this.organizationForm.removeControl(name);
    this.logInForm.removeControl(name);
    this.paymentForm.removeControl(name);

    if (name == "contact" || name == "org") {
      if (!this.organizationForm.get('contact')) {
        this.organizationForm.addControl(name, form);
      }

      if (!this.organizationForm.get('org')) {
        this.organizationForm.addControl(name, form);
      }

      this.patchValueForOrganizationInfo();
    }

    if (name == 'org') {
      (this.organizationForm.get('org') as FormGroup).controls['orgName'].valueChanges.pipe(debounceTime(1000)).subscribe((val) => {
        this.onOrgNameEntered(val);
      });
    }

    if (name == "account") {
      if (!this.logInForm.get('account')) {
        this.logInForm.addControl(name, form);
      }

      this.patchValueForLogInInfo();

      if (this.trialData && this.trialData.loginPrefix) {
        (this.logInForm.get('account') as FormGroup).controls['usernamePrefix'].setValue(this.trialData.loginPrefix);
      }
    }

    if (name == "payment" && !this.paymentForm.get('payment')) {
      this.paymentForm.addControl(name, form);
    }
  }

  onOrgNameEntered(val: string) {
    this.trialService.getPrefixSuggestion(val).subscribe(suggestion => {
      this.loginPrefix = suggestion.prefix;
    })
  }

  onNext() {
    switch (this.activeStep) {
      case 1:
        this.handleSetUpOrganizationInfo();
        break;
      case 2:
        this.handleSetUpLogInInfo();
        break;
      case 3:
        this.handleSetUpPaymentMethodInfo();
        break;
      default:
        break;
    }
  }

  onBack() {
    this.prevStep = this.activeStep;
    this.activeStep -= 1;
  }

  patchValueForOrganizationInfo() {
    if (this.organizationData) {
      const { contact, org } = this.organizationData;
      (this.organizationForm.get('contact') as FormGroup).patchValue(contact);
      (this.organizationForm.get('org') as FormGroup).patchValue(org);
    }
  }

  patchValueForLogInInfo() {
    if (this.loginData) {
      const { account } = this.loginData;
      (this.logInForm.get('account') as FormGroup).patchValue(account);
    }
  }

  handleSetUpOrganizationInfo() {
    this.organizationForm.markAsTouched();

    if (this.organizationForm.valid) {
      
      this.organizationData = { ...this.organizationForm.value };

      let contact = this.organizationForm.value.contact;
      let address = this.organizationForm.value.org.address;
      let org = this.organizationForm.value.org;

      this.trialData = new TrialRegistration();
      this.trialData.acceptsTerms = this.organizationForm.value.terms;
      this.trialData.city = address.city;
      this.trialData.countryCode = address.country;
      this.trialData.email = contact.email;
      this.trialData.firstName = contact.firstName;
      this.trialData.lastName = contact.lastName;
      this.trialData.organizationName = org.orgName;
      this.trialData.phone = contact.ext == "" ? contact.phone : contact.phone + " ext. " + contact.ext;
      this.trialData.postalCode = address.zip;
      this.trialData.stateCode = address.state;
      this.trialData.street1 = address.street;
      this.trialData.street2 = address.suite;
      this.trialData.loginPrefix = this.loginPrefix;
      //this.trialData.industry = org.industry;

      if (!this.trialData || !this.trialData.organizationName) {
        this.activeStep = 1;
        return;
      }
  
      this.trialService.submitHubspotPartialForm(this.trialData).subscribe(hubspot => {
      });

      this.prevStep = this.activeStep;
      this.activeStep += 1;

    } else {    
      this.markAllTouched(this.organizationForm);
    }
  }

  handleSetUpLogInInfo() {
    this.logInForm.markAsTouched();
    if (this.logInForm.valid) {
      
      this.loginData = { ...this.logInForm.value };
      let account = this.logInForm.value.account;
      this.trialData.password = account.password;
      this.trialData.confirmPassword = account.passwordConfirm;
      this.trialData.loginPrefix = account.usernamePrefix;
      this.trialData.username = account.loginId;
      this.trialData.iPadPin = account.pin;
      this.trialData.iPadPinConfirm = account.pinConfirm;
      
      this.prevStep = this.activeStep;
      this.activeStep += 1;

    } else {
      this.markAllTouched(this.logInForm);
    }
  }

  handleSetUpPaymentMethodInfo() {
    this.shouldShowSpinner = true;
    if (!this.isStripeCardValid) {
      this.shouldShowSpinner = false;
      this.showErrorMessage = true;
      return;
    }

    this.showErrorMessage = false;
    this.paymentService.generatePaymentMethod();
  }


  startTrialWithPaymentMethod(paymentMethodId: string) {
    if (paymentMethodId) {
      this.isStripeCardValid = true;
      this.showErrorMessage = false;
      this.shouldShowSpinner = true;

      this.trialData.selectedPlan = 'catalyst';
      this.trialService.registerTrial(this.trialData).subscribe(org => {
        this.usernamePrefix = org.usernamePrefix;

        this.trialService.startTrialWithPaymentMethod(
          this.usernamePrefix, 
          paymentMethodId, 
          'catalyst')
        .subscribe(trial => {
            this.shouldShowSpinner = false;
            this.shouldShowModal = true;
          }, error => {
            this.shouldShowSpinner = false;
            this.shouldShowModal = false;
          }
        );

        this.trialService.submitHubspotCombinedForm(this.trialData).subscribe(hubspot => {});
      }, error => {
        this.shouldShowModal = false;
        this.shouldShowSpinner = false;
      });
    } else {
      this.shouldShowSpinner = false;
      this.shouldShowModal = false;
      this.isStripeCardValid = false;
      this.showErrorMessage = true;
    }
  }

  startTrial() {
    this.trialService.startTrial(this.usernamePrefix)
      .subscribe(trial => {
          this.shouldShowSpinner = false;
          this.shouldShowModal = true;
        }, error => {
          this.shouldShowSpinner = false;
        }
      );
  }

  onGetStarted() {
    location.replace("https://secure.datafinch.com")
  }


  handleStripeCardChange(isCompleted: boolean) {
    this.isStripeCardValid = isCompleted;
  }

  handlePlanSelected(plan: string) {
    this.planSelected = plan;
  }


  markAllTouched(group: FormGroup) {
    var controls = Object.keys(group.controls);
    for(var i = 0; i < controls.length; i++) {
      var currentControl = group.controls[controls[i]];
      if(currentControl instanceof FormGroup) {
        this.markAllTouched(currentControl);
      } else {
        currentControl.markAsTouched();
      }
    }
  }

  ngOnInit() {
    this.fixHeaderAt = document.documentElement.clientWidth < 660 ? 360 : 240;
    this.scrollPosition = this.document.documentElement.scrollTop;
    localStorage.clear();
  }

  @HostListener("window:scroll", [])
  onWindowScroll() {
    this.scrollPosition = this.document.documentElement.scrollTop;
  }

  @HostListener("window:resize", [])
  onWindowResize() {
    this.fixHeaderAt = document.documentElement.clientWidth < 660 ? 360 : 240;
  }
}
